import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { extractEdgeFunctionError } from "@/lib/edgeFunctionError";
import { friendlyToast } from "@/lib/friendlyError";

async function spotAction(action: string, payload: Record<string, any> = {}) {
  const { data, error } = await supabase.functions.invoke("spot-trading", {
    body: { action, ...payload },
  });
  if (error || data?.error) {
    const msg = await extractEdgeFunctionError(error, data, "Something went wrong");
    throw new Error(msg);
  }
  return data;
}

export function useSpotMarkets() {
  return useQuery({
    queryKey: ["spot-markets"],
    queryFn: () => spotAction("get_markets"),
    staleTime: 30_000,
    select: (d) => d.markets || [],
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normalize a route param to the canonical market symbol (e.g. "btc-usdt" → "BTC_USDT"). */
export function normalizeMarketSymbol(value: string): string {
  return value.trim().replace(/[-/\s]/g, "_").toUpperCase();
}

/** Compact form used for forgiving matching (e.g. "BTC_USDT" → "BTCUSDT"). */
export function compactMarketSymbol(value: string): string {
  return value.trim().replace(/[-/_\s]/g, "").toUpperCase();
}

/** Resolve a route param against the known markets, tolerating compact symbols like "BTCUSDT". */
export function resolveMarketSymbol(value: string | undefined, markets: any[]): string | undefined {
  return resolveMarket(value, markets).symbol;
}

/** Resolve a route param and report whether it matched the requested pair exactly. */
export function resolveMarket(
  value: string | undefined,
  markets: any[]
): { symbol?: string; exact: boolean } {
  if (!value) return { symbol: markets?.[0]?.symbol, exact: true };
  const normalized = normalizeMarketSymbol(value);
  const exact = markets?.find((m: any) => m.symbol === normalized);
  if (exact) return { symbol: exact.symbol, exact: true };
  const compact = compactMarketSymbol(value);
  const loose = markets?.find((m: any) => compactMarketSymbol(m.symbol) === compact);
  // Loose match is still the pair the user asked for, just written differently.
  if (loose) return { symbol: loose.symbol, exact: true };
  // Fall back to the first available market rather than a dead end.
  return { symbol: markets?.length ? markets[0].symbol : undefined, exact: false };
}



export function useSpotMarket(symbolOrId?: string) {
  return useQuery({
    queryKey: ["spot-market", symbolOrId],
    queryFn: () =>
      spotAction(
        "get_market",
        symbolOrId && UUID_RE.test(symbolOrId)
          ? { market_id: symbolOrId }
          : { symbol: normalizeMarketSymbol(symbolOrId || "") }
      ),
    enabled: !!symbolOrId,
    select: (d) => d.market,
  });
}


export function useOrderbook(marketId?: string, depth = 20) {
  return useQuery({
    queryKey: ["spot-orderbook", marketId],
    queryFn: () => spotAction("get_orderbook", { market_id: marketId, depth }),
    enabled: !!marketId,
    refetchInterval: 10_000,
  });
}

export function useSpotTrades(marketId?: string, limit = 50) {
  return useQuery({
    queryKey: ["spot-trades", marketId],
    queryFn: () => spotAction("get_trades", { market_id: marketId, limit }),
    enabled: !!marketId,
    refetchInterval: 15_000,
    select: (d) => d.trades || [],
  });
}

export function useSpotCandles(marketId?: string, interval = "1h") {
  return useQuery({
    queryKey: ["spot-candles", marketId, interval],
    queryFn: () => spotAction("get_candles", { market_id: marketId, interval, limit: 500 }),
    enabled: !!marketId,
    staleTime: 30_000,
    select: (d) => d.candles || [],
  });
}

export function useSpotBalances(baseAsset?: string, quoteAsset?: string) {
  return useQuery({
    queryKey: ["spot-balances", baseAsset, quoteAsset],
    queryFn: () => spotAction("get_balances", { base_asset: baseAsset, quote_asset: quoteAsset }),
    enabled: !!baseAsset && !!quoteAsset,
    refetchInterval: 5000,
  });
}

export function useMySpotOrders(marketId?: string, status?: string) {
  return useQuery({
    queryKey: ["spot-my-orders", marketId, status],
    queryFn: () => spotAction("get_my_orders", { market_id: marketId, status, limit: 100 }),
    enabled: !!marketId,
    select: (d) => d.orders || [],
  });
}

export function useMySpotTrades(marketId?: string) {
  return useQuery({
    queryKey: ["spot-my-trades", marketId],
    queryFn: () => spotAction("get_my_trades", { market_id: marketId, limit: 100 }),
    enabled: !!marketId,
    select: (d) => d.trades || [],
  });
}

export function usePlaceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      market_id: string;
      side: "buy" | "sell";
      order_type: "limit" | "market";
      price?: number;
      quantity: number;
      client_order_id?: string;
      time_in_force?: string;
    }) => spotAction("place_order", params),
    onSuccess: (data) => {
      const status = data?.order?.status;
      if (status === "filled") toast.success("Order filled!");
      else if (status === "partially_filled") toast.success("Order partially filled");
      else toast.success("Order placed");
      qc.invalidateQueries({ queryKey: ["spot-my-orders"] });
      qc.invalidateQueries({ queryKey: ["spot-orderbook"] });
      qc.invalidateQueries({ queryKey: ["spot-trades"] });
      qc.invalidateQueries({ queryKey: ["spot-markets"] });
      qc.invalidateQueries({ queryKey: ["spot-balances"] });
    },
    onError: (err: any) => friendlyToast.error(err),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order_id: string) => spotAction("cancel_order", { order_id }),
    onSuccess: () => {
      toast.success("Order canceled");
      qc.invalidateQueries({ queryKey: ["spot-my-orders"] });
      qc.invalidateQueries({ queryKey: ["spot-orderbook"] });
      qc.invalidateQueries({ queryKey: ["spot-balances"] });
    },
    onError: (err: any) => friendlyToast.error(err),
  });
}

// Admin hooks
export function useAdminSpotOrders(marketId?: string, status?: string) {
  return useQuery({
    queryKey: ["spot-admin-orders", marketId, status],
    queryFn: () => spotAction("get_all_orders", { market_id: marketId, status, limit: 200 }),
    select: (d) => d.orders || [],
  });
}

export function useAdminSpotTrades(marketId?: string) {
  return useQuery({
    queryKey: ["spot-admin-trades", marketId],
    queryFn: () => spotAction("get_all_trades", { market_id: marketId, limit: 200 }),
    select: (d) => d.trades || [],
  });
}

export function useSpotFeeTiers() {
  return useQuery({
    queryKey: ["spot-fee-tiers"],
    queryFn: () => spotAction("get_fee_tiers"),
    select: (d) => d.tiers || [],
  });
}

export function useManageMarket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: any) => spotAction("manage_market", params),
    onSuccess: () => {
      toast.success("Market saved");
      qc.invalidateQueries({ queryKey: ["spot-markets"] });
    },
    onError: (err: any) => friendlyToast.error(err),
  });
}

export function useAdminCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { order_id: string; reason?: string }) =>
      spotAction("admin_cancel_order", params),
    onSuccess: () => {
      toast.success("Order force-canceled");
      qc.invalidateQueries({ queryKey: ["spot-admin-orders"] });
    },
    onError: (err: any) => friendlyToast.error(err),
  });
}

export function useManageFeeTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tiers: any[]) => spotAction("manage_fee_tiers", { tiers }),
    onSuccess: () => {
      toast.success("Fee tiers updated");
      qc.invalidateQueries({ queryKey: ["spot-fee-tiers"] });
    },
    onError: (err: any) => friendlyToast.error(err),
  });
}
